const VALID_EXECUTE_FIELD_IN = new Set(["path", "query", "body", "formData", "header", "cookie"]);

export const normalizeFieldIn = (raw) => {
  if (raw == null) return undefined;
  const s = String(raw).trim();
  if (!s) return undefined;
  const low = s.toLowerCase();
  if (low === "formdata") return "formData";
  if (VALID_EXECUTE_FIELD_IN.has(s)) return s;
  if (VALID_EXECUTE_FIELD_IN.has(low)) return low === "formdata" ? "formData" : low;
  return undefined;
};

export const fieldInFromDict = (f) => {
  if (!f || typeof f !== "object") return undefined;
  return normalizeFieldIn(f.in ?? f.paramIn ?? f.schema?.in);
};

export const resolveFieldIn = (proposedField, catalogField) =>
  fieldInFromDict(proposedField) || fieldInFromDict(catalogField) || "path";

export const catalogFieldsByName = (action) => {
  const fields = action?.config?.fields || action?.fields || [];
  const map = {};
  if (!Array.isArray(fields)) return map;
  for (const f of fields) {
    const name = f?.name || f?.title;
    if (!name) continue;
    map[name] = f;
    map[String(name).toLowerCase()] = f;
  }
  return map;
};

const catalogBodyParent = (field) => {
  if (!field || typeof field !== "object") return null;
  const name = field.name || field.title;
  if (!name) return null;
  const inv = fieldInFromDict(field);
  const schema = field.schema || {};
  if ((inv == null || inv === "formData" || inv === "body" || name === "body") && schema.type === "object") {
    return String(name);
  }
  return null;
};

const parseObjectValue = (val) => {
  if (val == null) return null;
  if (typeof val === "object" && !Array.isArray(val)) return val;
  if (typeof val === "string") {
    const s = val.trim();
    if (!s.startsWith("{") && !s.startsWith("[")) return null;
    try {
      const parsed = JSON.parse(s);
      return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }
  return null;
};

/**
 * Expand RJSF body object fields for brain TOOLS (workflow-style dotted keys).
 */
export const expandBrainToolFields = (fields = []) => {
  const out = [];
  for (const f of fields) {
    if (!f || typeof f !== "object") continue;
    const name = f.name || f.title;
    if (!name) continue;
    const parent = catalogBodyParent(f);
    if (parent) {
      const bodyIn = resolveFieldIn(f, f);
      const props = f.schema?.properties || {};
      Object.keys(props).forEach((propKey) => {
        const propSchema = props[propKey] || {};
        if (propSchema.hidden === true) return;
        out.push({
          name: `${parent}.${propKey}`,
          in: bodyIn,
          parent,
          property: propKey,
        });
      });
      continue;
    }
    out.push({ name, in: resolveFieldIn(f, f) });
  }
  return out;
};

/**
 * Merge body.* / flat body props into one formData body field before executeAction.
 */
export const coalesceExecuteFieldsForAction = (action, rows = []) => {
  if (!Array.isArray(rows) || rows.length === 0) return rows;
  const catalog = catalogFieldsByName(action);
  let bodyField = null;
  let bodyIn = "formData";
  let bodyProps = new Set();

  Object.values(catalog).forEach((af) => {
    if (bodyField) return;
    const parent = catalogBodyParent(af);
    if (!parent) return;
    bodyField = parent;
    bodyIn = resolveFieldIn(af, af);
    const props = af.schema?.properties || {};
    bodyProps = new Set(Object.keys(props).map(String));
  });

  if (!bodyField) return rows;

  const bodyValues = {};
  const otherRows = [];

  rows.forEach((row) => {
    if (!row || typeof row !== "object") return;
    const name = String(row.name || row.title || "");
    if (!name) return;
    const inv = fieldInFromDict(row) || "path";
    const val = row.value;

    if (name.startsWith(`${bodyField}.`)) {
      bodyValues[name.split(".", 1)[1]] = val;
      return;
    }
    if (name === bodyField && (inv === "body" || inv === "formData")) {
      const obj = parseObjectValue(val);
      if (obj) Object.assign(bodyValues, obj);
      else if (val != null && val !== "") bodyValues[name] = val;
      return;
    }
    if (bodyProps.has(name) && (inv === "body" || inv === "formData" || inv === "path")) {
      bodyValues[name] = val;
      return;
    }
    otherRows.push(row);
  });

  if (Object.keys(bodyValues).length > 0) {
    const cat = catalog[bodyField] || catalog[String(bodyField).toLowerCase()];
    otherRows.push({
      name: bodyField,
      in: bodyIn,
      value: bodyValues,
      ...(cat?.schema ? { schema: cat.schema } : {}),
    });
  }

  return otherRows;
};

export const normalizeExecuteFieldRow = (proposedField, catalogField) => {
  if (!proposedField || typeof proposedField !== "object") return null;
  const name = proposedField.name || proposedField.title;
  if (!name) return null;
  let value = proposedField.value;
  // Recover if HTTP Request was stored as a JSON string.
  if (String(name).toLowerCase() === "http request" && typeof value === "string") {
    const s = value.trim();
    if (s.startsWith("{") && s.endsWith("}")) {
      try {
        const parsed = JSON.parse(s);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) value = parsed;
      } catch {
        /* keep string */
      }
    }
  }
  const row = {
    name,
    in: resolveFieldIn(proposedField, catalogField),
    value,
    ...(catalogField?.schema ? { schema: catalogField.schema } : {}),
  };
  if (proposedField.required === true || catalogField?.required === true) {
    row.required = true;
  } else if (String(name).toLowerCase() === "http request") {
    row.required = true;
  }
  return row;
};

export const normalizeExecuteFieldsForAction = (action, proposedFields) => {
  const catalog = catalogFieldsByName(action);
  const rows = [];

  if (Array.isArray(proposedFields) && proposedFields.length > 0) {
    proposedFields.forEach((f) => {
      if (!f || typeof f !== "object") return;
      const name = f.name || f.title;
      if (!name) return;
      const cat = catalog[name] || catalog[String(name).toLowerCase()];
      const row = normalizeExecuteFieldRow(f, cat);
      if (row) rows.push(row);
    });
    return coalesceExecuteFieldsForAction(action, rows);
  }

  const configFields = action?.config?.fields || action?.fields || [];
  if (!Array.isArray(configFields)) return rows;

  configFields.forEach((af) => {
    if (!af || typeof af !== "object") return;
    const name = af.name || af.title;
    if (!name) return;
    const val = af.value;
    if (val == null || val === "" || String(val) === "Let agent pick value") return;
    const row = normalizeExecuteFieldRow({ name, value: val }, af);
    if (row) rows.push(row);
  });

  return coalesceExecuteFieldsForAction(action, rows);
};

const UNSET_PLACEHOLDER = "let agent pick value";

export const coerceKeyValueRows = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string") return value;
  const text = value.trim();
  if (!text.startsWith("[")) return value;
  try {
    const parsed = JSON.parse(text);
    return Array.isArray(parsed) ? parsed : value;
  } catch {
    return value;
  }
};

const isHumanErrorText = (value) => {
  if (typeof value !== "string") return false;
  const text = value.trim();
  if (text.length < 3 || text.length > 300) return false;
  if (/^https?:\/\//i.test(text)) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) return false;
  if (/^\d+$/.test(text)) return false;
  if (!text.includes(" ") && text.length > 24) return false;
  return true;
};

const collectHumanErrorStrings = (node, out, depth) => {
  if (depth > 3 || out.length >= 3 || node == null) return;
  if (typeof node === "string") {
    if (isHumanErrorText(node)) out.push(node.trim());
    return;
  }
  if (Array.isArray(node)) {
    for (const item of node.slice(0, 8)) {
      collectHumanErrorStrings(item, out, depth + 1);
      if (out.length >= 3) return;
    }
    return;
  }
  if (typeof node === "object") {
    for (const value of Object.values(node).slice(0, 12)) {
      collectHumanErrorStrings(value, out, depth + 1);
      if (out.length >= 3) return;
    }
  }
};

export const httpStatusFromOutput = (output) => {
  if (!output || typeof output !== "object") return null;
  for (const key of ["status_code", "statusCode", "status"]) {
    const code = Number(output[key]);
    if (Number.isFinite(code) && code >= 400) return code;
  }
  const message = output.message;
  if (message && typeof message === "object") {
    const code = Number(message.status);
    if (Number.isFinite(code) && code >= 400) return code;
  }
  return null;
};

/** HTTP status plus short prose from the error body. No connector-specific field list. */
export const describeApiFailure = (output) => {
  if (!output || typeof output !== "object") return "";
  const status = httpStatusFromOutput(output);
  const err = typeof output.error === "string" ? output.error.trim() : "";
  if (err.toUpperCase().startsWith("HTTP ") && isHumanErrorText(err)) return err.slice(0, 240);
  const failed = status != null || output.ok === false || Boolean(err);
  if (!failed) return "";
  let focus = output.message != null ? output.message : output;
  if (typeof focus === "string") {
    const text = focus.trim();
    if (text.startsWith("{") || text.startsWith("[")) {
      try {
        const parsed = JSON.parse(text);
        if (parsed && typeof parsed === "object") focus = parsed;
      } catch {
        /* keep the string */
      }
    }
  }
  const texts = [];
  if (isHumanErrorText(err)) texts.push(err);
  collectHumanErrorStrings(focus, texts, 0);
  const seen = new Set();
  const unique = [];
  for (const text of texts) {
    const key = text.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(text);
    if (unique.length >= 2) break;
  }
  const hint = unique.join("; ").slice(0, 200);
  if (status && hint) return `HTTP ${status}: ${hint}`.slice(0, 240);
  if (status) return `HTTP ${status}`;
  return hint.slice(0, 240);
};

export const priorStepFailed = (step) => {
  if (!step || typeof step !== "object") return false;
  const output = step.output;
  const receipt = step.receipt;
  if (receipt && receipt.ok === false) return true;
  if (!output || typeof output !== "object") return false;
  if (output.ok === false) return true;
  if (httpStatusFromOutput(output) != null) return true;
  if (typeof output.error === "string" && output.error.trim()) return true;
  return false;
};

export const actionIdsNeedingRevision = (priorSteps) => {
  const ids = new Set();
  for (const step of priorSteps || []) {
    if (!priorStepFailed(step)) continue;
    const aid = String(step?.action_id || step?.receipt?.action_id || "");
    if (aid && !aid.startsWith("_")) ids.add(aid);
  }
  return ids;
};

const secretFieldName = (name) =>
  /(authorization|api[-_]?key|token|secret|password|credential)/i.test(String(name || ""));

const compactRequestValue = (value, depth = 0) => {
  if (depth > 4) return null;
  if (value == null || typeof value === "boolean" || typeof value === "number") return value;
  if (typeof value === "string") return value.length > 140 ? `${value.slice(0, 140)}…` : value;
  if (Array.isArray(value)) {
    return value
      .slice(0, 12)
      .map((item) => compactRequestValue(item, depth + 1))
      .filter((item) => item != null);
  }
  if (typeof value === "object") {
    if ("key" in value && "value" in value && Object.keys(value).length <= 6) {
      const key = String(value.key || "");
      if (secretFieldName(key)) return { key, value: "[redacted]" };
      return { key, value: compactRequestValue(value.value, depth + 1) };
    }
    const out = {};
    for (const [key, item] of Object.entries(value).slice(0, 12)) {
      if (String(key).toLowerCase() === "headers" || secretFieldName(key)) continue;
      const compact = compactRequestValue(item, depth + 1);
      if (compact != null) out[key] = compact;
    }
    return Object.keys(out).length ? out : null;
  }
  return String(value).slice(0, 140);
};

const unresolvedPlaceholderPaths = (fields) => {
  const paths = [];
  const walk = (node, path) => {
    if (paths.length >= 8) return;
    if (typeof node === "string" && node.trim().toLowerCase() === UNSET_PLACEHOLDER) {
      paths.push(path || "value");
      return;
    }
    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        const label = item && typeof item === "object" && item.key ? String(item.key) : String(index);
        walk(item, `${path}[${label}]`);
      });
      return;
    }
    if (node && typeof node === "object") {
      Object.entries(node).forEach(([key, item]) => {
        if (key === "schema" || key === "description") return;
        walk(item, path ? `${path}.${key}` : key);
      });
    }
  };
  (Array.isArray(fields) ? fields : []).forEach((field) => {
    if (!field || typeof field !== "object") return;
    walk(field.value, String(field.name || "field"));
  });
  return paths;
};

const unsubstitutedPathTokens = (value) => {
  const found = [];
  const walk = (node) => {
    if (found.length >= 8) return;
    if (typeof node === "string") {
      const re = /\{([^{}/]+)\}/g;
      let match;
      while ((match = re.exec(node))) {
        const token = String(match[1] || "").trim();
        if (token && !found.includes(token)) found.push(token);
      }
      return;
    }
    if (Array.isArray(node)) node.forEach(walk);
    else if (node && typeof node === "object") Object.values(node).forEach(walk);
  };
  walk(value);
  return found;
};

export const compactSentRequest = (fields) => {
  if (!Array.isArray(fields)) return null;
  const rows = [];
  for (const field of fields) {
    if (!field || typeof field !== "object") continue;
    const name = String(field.name || "").slice(0, 80);
    if (!name || secretFieldName(name)) continue;
    const value = compactRequestValue(field.value);
    if (value == null) continue;
    rows.push({ name, value });
    if (rows.length >= 8) break;
  }
  if (!rows.length) return null;
  const body = { fields: rows };
  const unresolved = unresolvedPlaceholderPaths(fields);
  if (unresolved.length) body.unresolved_placeholders = unresolved;
  const tokens = unsubstitutedPathTokens(fields);
  if (tokens.length) body.unsubstituted_path_tokens = tokens;
  return body;
};

export const requestFingerprint = (fields) => {
  try {
    return JSON.stringify(compactSentRequest(fields) || {});
  } catch {
    return "";
  }
};

export const matchingFailedRequest = (priorSteps, actionId, fields) => {
  const fingerprint = requestFingerprint(fields);
  if (!fingerprint || fingerprint === "{}") return null;
  const steps = Array.isArray(priorSteps) ? priorSteps : [];
  for (let i = steps.length - 1; i >= 0; i -= 1) {
    const step = steps[i];
    if (String(step?.action_id || "") !== String(actionId || "")) continue;
    if (!priorStepFailed(step)) continue;
    const prev = step?.output?.sent_request?.fields || step?.sent_request?.fields;
    if (requestFingerprint(prev) === fingerprint) return step;
  }
  return null;
};

const usableRequestScalar = (value) => {
  const text = String(value || "").trim();
  return Boolean(text) && text.toLowerCase() !== UNSET_PLACEHOLDER;
};

/**
 * Merge a proposed HTTP request into the configured one.
 * Method and URL stay put until a prior call for this action was rejected.
 */
export const mergeCustomRequestValue = (base, rows, { allowRevision = false } = {}) => {
  const next = base && typeof base === "object" ? { ...base } : {};
  const byName = (name) =>
    (Array.isArray(rows) ? rows : []).find(
      (row) => String(row?.name || "").toLowerCase() === name,
    );
  const brainFull = byName("http request");
  if (brainFull && brainFull.value && typeof brainFull.value === "object") {
    const incoming = brainFull.value;
    for (const key of ["params", "headers", "stringBody"]) {
      if (incoming[key] != null) {
        next[key] = key === "params" ? coerceKeyValueRows(incoming[key]) : incoming[key];
      }
    }
    const canReplaceUrl = allowRevision || !next.url || String(next.url) === "Let agent pick value";
    if (usableRequestScalar(incoming.url) && canReplaceUrl) next.url = incoming.url;
    if (incoming.requestType && (allowRevision || !next.requestType)) {
      next.requestType = incoming.requestType;
    }
  }
  const paramsRow = byName("http request.params");
  if (paramsRow) next.params = coerceKeyValueRows(paramsRow.value);
  const bodyRow = byName("http request.stringbody");
  if (bodyRow) next.stringBody = bodyRow.value;
  if (allowRevision) {
    const urlRow = byName("http request.url");
    if (usableRequestScalar(urlRow?.value)) next.url = urlRow.value;
    const typeRow = byName("http request.requesttype");
    if (usableRequestScalar(typeRow?.value)) next.requestType = typeRow.value;
  }
  return next;
};

export const sampleSuccessfulCalls = (runs, actionIds, limit = 3) => {
  const wanted = actionIds instanceof Set ? actionIds : new Set(actionIds || []);
  if (!wanted.size) return [];
  const exemplars = [];
  const seen = new Set();
  for (const run of Array.isArray(runs) ? runs : []) {
    for (const step of run?.steps || []) {
      if (!step || step.success !== true) continue;
      const aid = String(step.action_id || "");
      if (!wanted.has(aid)) continue;
      const fields = step.request_payload?.fields;
      if (!Array.isArray(fields) || !fields.length) continue;
      const request = compactSentRequest(fields);
      if (!request) continue;
      const sig = JSON.stringify(request).slice(0, 500);
      if (seen.has(sig)) continue;
      seen.add(sig);
      exemplars.push({
        action_id: aid,
        action_key: String(step.action_key || "").slice(0, 80),
        request,
      });
      if (exemplars.length >= limit) return exemplars;
    }
  }
  return exemplars;
};

export const serializeExecuteFieldValue = (name, inv, rawVal, catalogField) => {
  if (rawVal == null) return "";
  const location = inv || "path";
  const schemaType = catalogField?.schema?.type;
  const nameLow = String(name || "").toLowerCase();
  // Custom Request "HTTP Request" (and other object-schema fields) must stay objects —
  // executeAction expects value: { requestType, url, stringBody }, not a JSON string.
  const isObjectField =
    schemaType === "object" || nameLow === "http request" || nameLow === "body";
  const isBodyContainer =
    location === "formData" ||
    (location === "body" && (name === "body" || schemaType === "object"));
  if (typeof rawVal === "object") {
    if (isBodyContainer || isObjectField) return rawVal;
    return JSON.stringify(rawVal);
  }
  return String(rawVal);
};
